/* eslint-disable @typescript-eslint/no-explicit-any */
import {Component, DestroyRef, Input, OnInit} from '@angular/core';
import {LookupService} from '../../common/service/lookup.service';
import {PlanModel, PlanModelService} from '../../common/service/plan-model.service';
import {PlanLodgementService} from '../../common/service/plan-lodgement.service';
import {NgEventBus} from 'ng-event-bus';
import {isUndefined} from '../../common/service/data-util';
import {isArray} from 'lodash-es';
import {InterestReconciliationService} from '../../common/service/interest-reconciliation.service';
import {AffectedLotReconciliationService} from '../../common/service/affected-lot-reconciliation.service';
import {COMMON_PROPERTY, NEW_INTEREST_CREATED_BY, NON_LOT_COMMON_PROPERTY, SP_PREFIXED_PARENT_PLAN_TYPES} from '../../common/constants';
import {AlertModalService, ConfirmationModalService} from '../../common/component/modal';
import {NgClass} from '@angular/common';
import {NlrHasPermissionsPipe} from '../../common/pipe/nlr-has-permissions.pipe';
import {NlrPermissionsDisabledPipe} from '../../common/pipe/nlr-permissions-disabled-pipe';
import {NlrFormFieldHasErrorPipe} from '../../common/pipe/nlr-form-field-has-error.pipe';
import {ControlContainer, FormsModule, NgForm} from '@angular/forms';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {mergeMap, switchMap} from 'rxjs/operators';
import {of} from 'rxjs';
import {InterestService} from '../../design/services/features/interest.service';

@Component({
    selector: 'new-interests-notifications',
    imports: [NlrHasPermissionsPipe, NlrPermissionsDisabledPipe, NlrFormFieldHasErrorPipe, NgClass, FormsModule],
    templateUrl: './new-interests-notifications.component.html',
    styleUrl: './new-interests-notifications.component.scss',
    viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class NewInterestsNotificationsComponent implements OnInit {
    title = 'New Interests and Notifications';

    @Input() newInterestsUiConfig: unknown;
    @Input() newInterestsQuestionDisplay: unknown;

    newInterestsNotifications = [];
    interestSortOrderMap = [];
    interestCodes = [];
    model: PlanModel;

    constructor(
        private interestService: InterestService,
        private lookupService: LookupService,
        private planModelService: PlanModelService,
        private planLodgementService: PlanLodgementService,
        private eventBus: NgEventBus,
        private interestReconciliationService: InterestReconciliationService,
        private affectedLotReconciliationService: AffectedLotReconciliationService,
        private confirmationModalService: ConfirmationModalService,
        private alertModalService: AlertModalService,
        private destroyRef: DestroyRef
    ) {}

    ngOnInit(): void {
        this.model = this.planModelService.getModel();

        this.eventBus
            .on('nlr:plan-model-updated:*')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.model = this.planModelService.getModel();
            });

        this.eventBus
            .on('nlr:plan-model-updated:model')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this.planModelService.getModel().interests) {
                    this.populateNewInterests();
                }
            });

        this.eventBus
            .on('initiateNewInterests')
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(() => {
                    this.populateNewInterests();
                    return this.interestReconciliationService.reconcileInterests(
                        this.planModelService.getModel().formerTenures,
                        this.planModelService.getModel().interests
                    );
                }),
                switchMap(() => this.affectedLotReconciliationService.process())
            )
            .subscribe();

        this.initInterestCodes();
        this.interestSortOrderMap = this.lookupService.getLookups('interest').reduce((acc, l) => {
            acc[l.code] = parseInt(l.sortOrder);
            return acc;
        }, {});

        this.populateNewInterests();
    }

    addNewInterest() {
        this.interestService.openNewInterestModal(null).subscribe();
    }

    populateNewInterests() {
        if (this.planModelService.getModel().interests) {
            this.newInterestsNotifications.splice(
                0,
                this.newInterestsNotifications.length,
                ...this.planModelService
                    .getModel()
                    .interests.map(interest => this.createInterestNotificationFromInterest(interest))
                    .sort((i1, i2) => {
                        if (i1.code === i2.code) {
                            return i1.id - i2.id;
                        }
                        return this.interestSortOrderMap[i1.code] - this.interestSortOrderMap[i2.code];
                    })
            );
        }
    }

    getInterestIndex(code) {
        return this.interestCodes.indexOf(code);
    }

    initInterestCodes() {
        if (isUndefined(this.interestCodes)) {
            this.interestCodes = this.lookupService.getLookups('interest').map(l => l.code);
        }
    }

    getInterestSortOrder(code) {
        return this.interestSortOrderMap[code];
    }

    deleteInterest(newInterestNotification) {
        this.confirmationModalService
            .showModal({
                title: 'Warning',
                confirmationText: 'You are about to remove this interest. Are you sure you wish to continue?'
            })
            .pipe(
                mergeMap(action => {
                    if (action === 'yes') {
                        const iindex = this.planModelService.getModel().interests.indexOf(newInterestNotification.interest);
                        this.planModelService.getModel().interests.splice(iindex, 1);
                        const index = this.newInterestsNotifications.indexOf(newInterestNotification);
                        this.newInterestsNotifications.splice(index, 1);
                        return this.planModelService.save();
                    }
                    return of();
                })
            )
            .subscribe({
                next: (model: PlanModel | undefined) => {
                    if (model) {
                        this.eventBus.cast('initiateNewInterests');
                        this.alertModalService.createAlertMessage('The interest has been removed.', 'Ok', 'interest removed', 'success');
                    }
                },
                error: reason => {
                    this.alertModalService.createAlertMessage(reason);
                }
            });
    }

    editInterest(newInterestNotification: {interest: any}) {
        this.interestService.openNewInterestModal(newInterestNotification.interest).subscribe();
    }

    createInterestNotificationFromInterest(interest) {
        const newInterestNotification: any = {};

        newInterestNotification.id = interest.id;
        newInterestNotification.code = interest.interestCode.code;
        newInterestNotification.interest = interest;
        newInterestNotification.subject = interest.subject;
        newInterestNotification.purpose = interest.interestCode.purpose;
        newInterestNotification.statutoryReference = interest.interestCode.statutoryReference;
        newInterestNotification.origin = this.getOrigin(interest);
        newInterestNotification.benefitTo = this.getBenefitTo(interest);
        newInterestNotification.landBurdened = this.getLandBurdened(interest);
        newInterestNotification.userEnteredComments = interest.userEnteredComments;
        return newInterestNotification;
    }

    getOrigin(interest) {
        if (interest.createdBy === NEW_INTEREST_CREATED_BY.PLAN.CODE) {
            return NEW_INTEREST_CREATED_BY.PLAN.DISPLAY;
        } else if (interest.createdBy === NEW_INTEREST_CREATED_BY.DOCUMENT.CODE) {
            if (interest.originDocument) {
                return NEW_INTEREST_CREATED_BY.DOCUMENT.DISPLAY + ' ' + interest.originDocument;
            } else {
                return NEW_INTEREST_CREATED_BY.DOCUMENT.DISPLAY;
            }
        } else if (interest.createdBy === NEW_INTEREST_CREATED_BY.DOCUMENT_AND_PLAN.CODE) {
            if (interest.originDocument) {
                return NEW_INTEREST_CREATED_BY.DOCUMENT_AND_PLAN.DISPLAY + ' ' + interest.originDocument;
            } else {
                return NEW_INTEREST_CREATED_BY.DOCUMENT_AND_PLAN.DISPLAY;
            }
        }
        console.warn('Unable to find origin for interest %o', interest);
        return '';
    }

    getLandBurdened(interest) {
        if (interest.burdenedLots && isArray(interest.burdenedLots) && interest.burdenedLots.length > 0) {
            let burdened = '';
            // angular.forEach(interest.burdenedLots, function (lot) {
            interest.burdenedLots.forEach(lot => {
                if (!burdened) {
                    burdened = 'Lot/s ';
                } else {
                    burdened = burdened + ',';
                }
                burdened = burdened + lot.startLot;
                if (lot.endLot) {
                    burdened = burdened + '-' + lot.endLot;
                }
            });
            return [burdened];
        }

        if (interest.burdenedAffectedTenures && isArray(interest.burdenedAffectedTenures) && interest.burdenedAffectedTenures.length > 0) {
            const planLotMap = interest.burdenedAffectedTenures.reduce((acc, bat) => {
                if (!bat.affectedTenureLot && bat.affectedTenure.subjectLandType) {
                    const planNumber = 'NLOP';
                    if (!acc[planNumber]) {
                        acc[planNumber] = [];
                    }
                    acc[planNumber].push(bat.affectedTenure.subjectLandType.description + ' | ' + bat.affectedTenure.subjectLandDescription);
                } else {
                    const planNumber = SP_PREFIXED_PARENT_PLAN_TYPES.includes(bat.affectedTenure.parentPlanType.id)
                        ? 'SP' + bat.affectedTenure.parentPlan
                        : bat.affectedTenure.parentPlanType.id + bat.affectedTenure.parentPlan;
                    if (!acc[planNumber]) {
                        acc[planNumber] = [];
                    }
                    if (bat.affectedTenureLot.nonLotCommonProperty) {
                        acc[planNumber].push(NON_LOT_COMMON_PROPERTY);
                    } else {
                        if (bat.affectedTenureLot.parentLot) {
                            if (bat.affectedTenureLot.parentLotType === COMMON_PROPERTY) {
                                acc[planNumber].push(COMMON_PROPERTY + bat.affectedTenureLot.parentLot);
                            } else {
                                acc[planNumber].push(bat.affectedTenureLot.parentLot);
                            }
                        }
                    }
                }
                return acc;
            }, {});

            return Object.keys(planLotMap).map(planNumber => `${planNumber}: ${planLotMap[planNumber].join()}`);
        }
        return [''];
    }

    getBenefitTo(interest) {
        const benefitTo = {namedBeneficiary: [], lotsOnThisPlan: [], lotsOnOtherPlan: []};
        if (interest.benefits && interest.benefits.length > 0) {
            interest.benefits.forEach(benefit => {
                if (benefit.category === 'LOTS_ON_PLAN') {
                    if (benefit.lots && isArray(benefit.lots) && benefit.lots.length > 0) {
                        benefit.lots.forEach(lot => {
                            let lotName = lot.startLot;
                            if (lot.endLot) {
                                lotName += '-' + lot.endLot;
                            }

                            benefitTo.lotsOnThisPlan.push(lotName);
                        });
                    }
                } else if (benefit.category === 'LOTS_ON_OTHER_PLAN') {
                    const lotsOnOtherPlan = [];
                    if (benefit.lots && isArray(benefit.lots) && benefit.lots.length > 0) {
                        benefit.lots.forEach(lot => {
                            let lotName = lot.startLot;
                            if (lot.endLot) {
                                lotName += '-' + lot.endLot;
                            }
                            lotsOnOtherPlan.push(lotName);
                        });

                        benefitTo.lotsOnOtherPlan.push({planReference: benefit.planReference, lots: lotsOnOtherPlan});
                    }
                } else if (benefit.category === 'SELECTED_BENEFICIARY') {
                    benefitTo.namedBeneficiary.push(benefit.type?.name);
                } else if (benefit.category === 'LOCAL_GOVERNMENT_AUTHORITY') {
                    benefitTo.namedBeneficiary.push(benefit.lga);
                } else if (benefit.category === 'FREE_TEXT_BENEFICIARY' || benefit.category === 'ADDITIONAL_BENEFICIARY') {
                    benefitTo.namedBeneficiary.push(benefit.freeText);
                } else if (benefit.category === 'SEE_DOCUMENT') {
                    benefitTo.namedBeneficiary.push(benefit.category.replace('_', ' '));
                }
            });
        }
        return benefitTo;
    }

    hasInvalidPlanDetails() {
        return !this.planLodgementService.hasMinimumPlanDetails();
    }

    getError(notification) {
        if (!notification.interest || !notification.interest.interestCode) {
            return '';
        }

        const planPurpose = this.planModelService.getModel().planPurpose;

        if (
            (planPurpose &&
                planPurpose.parentTenureAssociationType === 'AFFECTED_TENURE' &&
                notification.interest.interestCode.burdensAffectedTenure.includes('AFFECTED_TENURE') &&
                notification.interest.burdenedAffectedTenures.length === 0) ||
            (notification.interest.interestCode.code &&
                notification.interest.benefits.some(benefit => benefit.category !== 'SEE_DOCUMENT') &&
                (notification.interest.sameOwner === null || notification.interest.sameOwner === undefined) &&
                this.lookupService
                    .getLookups('interest')
                    .some(
                        interest => interest.cadSameOwnerPlanPurpose === planPurpose.id && interest.code === notification.interest.interestCode.code
                    ))
        ) {
            return 'The Interests page is incomplete or data entered is invalid!';
        }
        return '';
    }

    isReadonly() {
        return this.planModelService.readonly;
    }
}
